import {
  CHART_COLORS,
  CHART_DIMENSIONS,
  CHART_IDS,
  TICK_AMOUNTS,
  LACTATION_STYLES,
} from "../constants/chartConstants";
import {
  extractAllDataValues,
  calculateMinMax,
  roundToNearest,
} from "./chartDataProcessors";

/**
 * Builds base chart configuration
 */
const buildBaseConfig = (chartType, chartLabelColor, id, name) => ({
  colors: CHART_COLORS,
  chart: {
    id,
    name,
    height: CHART_DIMENSIONS.HEIGHT,
    width: CHART_DIMENSIONS.WIDTH,
    type: chartType,
    toolbar: { show: false },
  },
  stroke: { curve: "straight" },
  dataLabels: {
    enabled: chartType === "line" ? false : true,
    style: {
      fontSize: "15px",
      fontWeight: "700",
      colors: chartLabelColor,
    },
  },
});

/**
 * Builds X-axis configuration
 */
const buildXAxis = (
  category,
  xaxistext,
  isNumeric = false,
  useWeeks = false,
  useFilteredWeeks = false,
) => {
  const baseConfig = {
    type: isNumeric ? "numeric" : "string",
    labels: {
      style: { fontSize: "15px" },
    },
    title: {
      text: xaxistext,
      style: {
        fontSize: "15px",
        fontWeight: "700",
      },
    },
    categories: category,
  };

  if (isNumeric && useWeeks) {
    if (useFilteredWeeks) {
      // For filtered weeks, use numeric type to show proportional spacing
      const allowedWeeks = [2, 4, 8, 15, 21, 25, 32, 37];
      return {
        ...baseConfig,
        type: "numeric",
        min: 0,
        max: 40,
        tickAmount: 40,
        tickPlacement: "on",
        labels: {
          ...baseConfig.labels,
          formatter: (value) => {
            const roundedValue = Math.round(value);
            // Only show labels for the specific weeks in our data
            return allowedWeeks.includes(roundedValue) ? roundedValue : "";
          },
        },
      };
    }
    // For full 44 weeks, show labels at intervals of 4
    return {
      ...baseConfig,
      tickAmount: 11,
      min: 0,
      max: 44,
      labels: {
        ...baseConfig.labels,
        formatter: (value) => {
          // Show labels at multiples of 4
          const numValue = Number(value);
          if (numValue % 4 === 0) {
            return Math.round(numValue);
          }
          return "";
        },
      },
    };
  }

  return {
    ...baseConfig,
    tickPlacement: "between",
  };
};

/**
 * Builds Y-axis configuration
 */
const buildYAxis = (yaxistext, options = {}) => {
  const { min = 0, max, decimalsInFloat, tickAmount, formatter } = options;

  const config = {
    min,
    labels: {
      style: { fontSize: "15px" },
    },
    title: {
      text: yaxistext,
      style: {
        fontSize: "15px",
        fontWeight: "700",
      },
    },
  };

  if (max !== undefined) config.max = max;
  if (decimalsInFloat !== undefined) config.decimalsInFloat = decimalsInFloat;
  if (tickAmount !== undefined) config.tickAmount = tickAmount;
  if (formatter) config.labels.formatter = formatter;

  return config;
};

/**
 * Builds legend configuration
 */
const buildLegend = (
  position = "top",
  customFormatter = null,
  markers = {},
  chartType = "line",
) => ({
  horizontalAlign: "right",
  position,
  showForSingleSeries: true,
  markers: {
    width: chartType === "line" ? 20 : 12,
    height: chartType === "line" ? 4 : 12,
    strokeWidth: 0,
    ...markers,
  },
  formatter:
    customFormatter ||
    function (seriesName, opts) {
      return opts.w.config.series[opts.seriesIndex].header;
    },
});

/**
 * Builds tooltip configuration
 */
const buildTooltip = (reportTab, customYFormatter = null) => {
  const tooltip = {
    x: { format: "dd/MM/yy" },
  };

  if (["periodcomparison", "subherdcomparison"].includes(reportTab)) {
    tooltip.y = {
      title: {
        formatter: (seriesName, opts) => {
          const header = opts.w.config.series.find(
            (d) => d.name === seriesName,
          )?.header;
          const pattern = /\d{4}-\d{2}-\d{2}/g;
          if (pattern.test(header)) {
            const match = header.match(pattern);
            if (match.length === 2) {
              return match[0] === match[1] ? match[0] : header;
            }
          }
          return header;
        },
      },
      formatter:
        customYFormatter ||
        ((val) => {
          if (val === null) return 0;
          return !isNaN(val) ? val.toFixed(2) : 0;
        }),
    };
  }

  return tooltip;
};

/**
 * Calculates Y-axis range for standard charts
 */
const calculateYAxisRange = (data, reportTab, chartId) => {
  const allValues = extractAllDataValues(data);
  if (allValues.length === 0) return { min: 0, max: 10, tickAmount: 1 };

  const { min: dataMin, max: dataMax } = calculateMinMax(allValues);

  if (reportTab === "snapsix") {
    const min = Math.max(0, roundToNearest(dataMin, 10) - 10);
    const max = roundToNearest(dataMax, 10) + 10;
    return {
      min,
      max,
      tickAmount: Math.floor((max - min) / 10),
    };
  }

  // For other tabs
  const min = 0;
  let max = roundToNearest(dataMax, 10) + 10;
  let divisor = 10;

  if ([CHART_IDS.AVG_DIM, CHART_IDS.DAYS_OPEN].includes(chartId)) {
    max = roundToNearest(dataMax, 20) + 20;
    divisor = 20;
  } else if (chartId === CHART_IDS.CALVING_INTERVAL) {
    max = roundToNearest(dataMax, 50) + 50;
    divisor = 50;
  }

  return {
    min,
    max,
    tickAmount: Math.floor((max - min) / divisor),
  };
};

/**
 * Calculates Y-axis configuration for component charts (fat, protein, etc.)
 */
const calculateComponentYAxis = (chartId, data) => {
  switch (chartId) {
    case CHART_IDS.PERCENT_FAT:
      return {
        tickAmount: TICK_AMOUNTS.PERCENT_FAT,
        max: TICK_AMOUNTS.PERCENT_FAT,
      };
    case CHART_IDS.PERCENT_PROTEIN:
      return {
        tickAmount: TICK_AMOUNTS.PERCENT_PROTEIN,
        max: TICK_AMOUNTS.PERCENT_PROTEIN,
      };
    case CHART_IDS.TOTAL_LBS:
    default:
      const allValues = extractAllDataValues(data);
      if (allValues.length === 0)
        return {
          tickAmount: TICK_AMOUNTS.TOTAL_LBS_DEFAULT,
          max: TICK_AMOUNTS.TOTAL_LBS_DEFAULT + 1,
        };

      const maxValue = Math.max(...allValues);
      const maxInt = Math.floor(maxValue);
      const yAxisMax = maxInt + 1;
      let tickAmount = maxInt;

      if (maxInt <= 4) {
        tickAmount = 4;
      } else if (maxInt > 20) {
        tickAmount = 15;
      } else {
        tickAmount = Math.min(maxInt, 15);
      }

      // Ensure tickAmount is at least equal to max to prevent duplicate labels
      if (tickAmount < yAxisMax) {
        tickAmount = yAxisMax;
      }

      return { tickAmount, max: yAxisMax };
  }
};

/**
 * Configures lactation curve specific settings
 */
const configureLactationCurve = (
  config,
  reportTab,
  lactationcurveminy,
  lactationcurvemaxy,
  convertPeerToNational,
  useWeeks = false,
  isLactationCurve = false,
  seriesData = null,
  useFilteredWeeks = false,
) => {
  const min = roundToNearest(lactationcurveminy, 10);
  const max = roundToNearest(lactationcurvemaxy, 10);
  const tickNumber = max - min >= 200 ? 20 : 10;

  // Set tickAmount based on xaxis categories length and configure label formatter
  const rawCategories = config.xaxis?.categories;
  const categoriesLength = Array.isArray(config.xaxis?.categories)
    ? config.xaxis.categories.length
    : 0;

  if (useWeeks) {
    if (categoriesLength > 0) {
      if (useFilteredWeeks) {
        // For filtered weeks, use numeric type for proportional spacing
        const allowedWeeks = [2, 4, 8, 15, 21, 25, 32, 37];
        config.xaxis = {
          ...config.xaxis,
          type: "numeric",
          min: 0,
          max: 40,
          tickAmount: 40,
          tickPlacement: "on",
          labels: {
            ...config.xaxis.labels,
            formatter: (value) => {
              const roundedValue = Math.round(value);
              // Only show labels for the specific weeks in our data
              return allowedWeeks.includes(roundedValue) ? roundedValue : "";
            },
          },
        };
      } else {
        // For full weeks, show labels at intervals of 4
        config.xaxis = {
          ...config.xaxis,
          tickAmount: rawCategories[categoriesLength - 1] / 4,
          labels: {
            ...config.xaxis.labels,
            formatter: (value) => {
              const numValue = Number(value);
              // Show labels at multiples of 4
              if (numValue % 4 === 0) {
                return Math.round(numValue);
              }
              return "";
            },
          },
        };
      }
    }
  }

  config.yaxis = {
    ...config.yaxis,
    min,
    max,
    decimalsInFloat: 2,
    tickAmount: Math.floor((max - min) / tickNumber),
    labels: {
      ...config.yaxis.labels,
      formatter: (val) => val.toFixed(0),
    },
  };

  config.tooltip = {
    ...config.tooltip,
    marker: { show: true },
    y: {
      title: {
        formatter: (seriesName, opts) => {
          const header = opts.w.config.series.find(
            (d) => d.name === seriesName,
          )?.header;
          if (
            reportTab === "periodcomparison" ||
            reportTab === "subherdcomparison"
          ) {
            const pattern = /\d{4}-\d{2}-\d{2}/g;
            if (header && pattern.test(header)) {
              const match = header.match(pattern);
              if (match && match.length === 2) {
                return match[0] === match[1] ? match[0] : header;
              }
            }
          } else if (convertPeerToNational) {
            return header?.replace("Peer Average", "National Average");
          }
          return header;
        },
      },
      formatter: (val) => {
        if (val === null) return 0;
        return !isNaN(val) ? val.toFixed(2) : 0;
      },
    },
  };

  // Determine colors, marker widths, and dash patterns based on reportTab and series
  let colors = CHART_COLORS;
  let markerWidths = 20;
  let dashArray = [];

  if (seriesData && Array.isArray(seriesData)) {
    const seriesCount = seriesData.length;

    if (isLactationCurve) {
      // Lactation curve configuration
      colors = CHART_COLORS;
      dashArray = LACTATION_STYLES.lactationCurve.dashArray.slice(
        0,
        seriesCount,
      );
      markerWidths = seriesData.map((_, index) => {
        if (index < 3) return 20; // First 3 series are solid
        if (index < 6) return 8; // Next 3 are medium dashed
        return 5; // Rest are short dashed
      });
    } else {
      // Lactation 1, 2, 3 charts - use report-specific styles
      const style = LACTATION_STYLES[reportTab] || {
        colors: CHART_COLORS,
        dashArray: Array(seriesCount).fill(null),
        markerWidths: Array(seriesCount).fill(20),
      };

      colors = style.colors.slice(0, seriesCount);
      dashArray = style.dashArray.slice(0, seriesCount);
      markerWidths = Array.isArray(style.markerWidths)
        ? style.markerWidths.slice(0, seriesCount)
        : 20;
    }
  }

  config.colors = colors;
  config.stroke = {
    curve: "straight",
    dashArray: dashArray,
  };
  config.legend = {
    ...config.legend,
    markers: {
      width: markerWidths,
      height: 4,
      strokeWidth: 0,
    },
  };

  return config;
};

/**
 * Main function to generate chart options
 */
export const getChartOptions = (params) => {
  const {
    chartType,
    chartLabelColor,
    category,
    id,
    name,
    xaxistext,
    yaxistext,
    reportTab,
    graphdataresponse,
    lactationcurveminy,
    lactationcurvemaxy,
    useavgdailymilkflag,
    reportResponse,
    useFilteredWeeks = false,
  } = params;

  let config = buildBaseConfig(chartType, chartLabelColor, id, name);

  const convertPeerToNational =
    reportTab === "benchmark" &&
    typeof reportResponse?.Peer_Footer_Definition?.Peer_Average_Availability !==
      "undefined";

  // Determine if using weeks
  const useWeeks =
    reportTab !== "benchmark" &&
    (useavgdailymilkflag || reportResponse?.Use_Avg_Daily_Milk);

  // Build base axes
  const isLactationChart = [
    CHART_IDS.LACTATION_1,
    CHART_IDS.LACTATION_2,
    CHART_IDS.LACTATION_3,
    CHART_IDS.LACTATION_CURVE,
  ].includes(id);

  config.xaxis = buildXAxis(
    category,
    xaxistext,
    isLactationChart && useWeeks,
    useWeeks,
    useFilteredWeeks,
  );
  config.yaxis = buildYAxis(yaxistext);
  config.legend = buildLegend("top", null, {}, chartType);
  config.tooltip = buildTooltip(reportTab);

  // Configure Y-axis based on chart type
  const componentCharts = [
    CHART_IDS.PERCENT_FAT,
    CHART_IDS.PERCENT_PROTEIN,
    CHART_IDS.TOTAL_LBS,
  ];

  if (componentCharts.includes(id)) {
    const yAxisConfig = calculateComponentYAxis(id, graphdataresponse);
    config.yaxis = { ...config.yaxis, ...yAxisConfig };

    config.yaxis.labels.formatter = (val) =>
      val === null ? 0 : val.toFixed(0);
    config.tooltip.y = {
      formatter: (val) => {
        if (val === null) return 0;
        return !isNaN(val) ? val.toFixed(2) : 0;
      },
    };
  } else if (
    [
      "snapsix",
      "snapshot",
      "benchmark",
      "periodcomparison",
      "subherdcomparison",
    ].includes(reportTab)
  ) {
    // Standard bar charts
    if (!componentCharts.includes(id) && graphdataresponse) {
      const yAxisRange = calculateYAxisRange(graphdataresponse, reportTab, id);
      config.yaxis = {
        ...config.yaxis,
        ...yAxisRange,
        decimalsInFloat: 0,
      };
    }
  }

  // Special configuration for lactation charts
  if (isLactationChart && id !== CHART_IDS.LACTATION_CURVE) {
    config = configureLactationCurve(
      config,
      reportTab,
      lactationcurveminy,
      lactationcurvemaxy,
      convertPeerToNational,
      useWeeks,
      false,
      graphdataresponse,
      useFilteredWeeks,
    );
  } else if (id === CHART_IDS.LACTATION_CURVE) {
    config = configureLactationCurve(
      config,
      reportTab,
      lactationcurveminy,
      lactationcurvemaxy,
      convertPeerToNational,
      useWeeks,
      true,
      graphdataresponse,
      useFilteredWeeks,
    );
  }

  return config;
};
